import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, PlusIcon, TrashIcon } from 'lucide-react';
import { InvoiceItem } from '../types/Invoice';
export function InvoiceForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const invoiceId = searchParams.get('id');
  const isEditMode = !!invoiceId;
  const [formData, setFormData] = useState({
    invoiceNumber: isEditMode ? 'INV-2024-001' : '',
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    issueDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    description: '',
    notes: '',
    status: 'draft' as const
  });
  const [items, setItems] = useState<InvoiceItem[]>([{
    id: '1',
    description: '',
    quantity: 1,
    unitPrice: 0,
    total: 0
  }]);
  const calculateItemTotal = (quantity: number, unitPrice: number) => {
    return quantity * unitPrice;
  };
  const calculateSubtotal = () => {
    return items.reduce((sum, item) => sum + item.total, 0);
  };
  const addItem = () => {
    const newItem: InvoiceItem = {
      id: Date.now().toString(),
      description: '',
      quantity: 1,
      unitPrice: 0,
      total: 0
    };
    setItems([...items, newItem]);
  };
  const removeItem = (id: string) => {
    if (items.length > 1) {
      setItems(items.filter(item => item.id !== id));
    }
  };
  const updateItem = (id: string, field: keyof InvoiceItem, value: string | number) => {
    setItems(items.map(item => {
      if (item.id === id) {
        const updatedItem = {
          ...item,
          [field]: value
        };
        if (field === 'quantity' || field === 'unitPrice') {
          updatedItem.total = calculateItemTotal(updatedItem.quantity, updatedItem.unitPrice);
        }
        return updatedItem;
      }
      return item;
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real app, this would save to an API
    console.log('Saving invoice:', {
      formData,
      items
    });
    navigate('/invoices');
  };
  const handleSaveAsDraft = () => {
    // In a real app, this would save as draft to an API
    console.log('Saving as draft:', {
      formData,
      items
    });
    navigate('/invoices');
  };
  return <div className="space-y-6" data-id="element-7117">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-7118">
        <button onClick={() => navigate('/invoices')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900" data-id="element-7119">
          <ArrowLeftIcon size={20} data-id="element-7120" />
          Back to Invoices
        </button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6" data-id="element-7121">
        {/* Main Form */}
        <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-7122">
          <h2 className="text-xl font-semibold text-gray-900 mb-6" data-id="element-7123">
            {isEditMode ? 'Edit Invoice' : 'Create New Invoice'}
          </h2>
          {/* Invoice Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-7124">
            <div data-id="element-7125">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-7126">
                Invoice Number
              </label>
              <input type="text" value={formData.invoiceNumber} onChange={e => setFormData({
              ...formData,
              invoiceNumber: e.target.value
            })} placeholder="Auto-generated if left empty" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-7127" />
            </div>
            <div data-id="element-7128">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-7129">
                Status
              </label>
              <select value={formData.status} onChange={e => setFormData({
              ...formData,
              status: e.target.value as 'draft' | 'sent'
            })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-7130">
                <option value="draft" data-id="element-7131">Draft</option>
                <option value="sent" data-id="element-7132">Send to Customer</option>
              </select>
            </div>
          </div>
          {/* Customer Information */}
          <div className="mb-6" data-id="element-7133">
            <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-7134">
              Customer Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-7135">
              <div data-id="element-7136">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-7137">
                  Customer Name *
                </label>
                <input type="text" required value={formData.customerName} onChange={e => setFormData({
                ...formData,
                customerName: e.target.value
              })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-7138" />
              </div>
              <div data-id="element-7139">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-7140">
                  Email *
                </label>
                <input type="email" required value={formData.customerEmail} onChange={e => setFormData({
                ...formData,
                customerEmail: e.target.value
              })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-7141" />
              </div>
              <div data-id="element-7142">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-7143">
                  Phone
                </label>
                <input type="tel" value={formData.customerPhone} onChange={e => setFormData({
                ...formData,
                customerPhone: e.target.value
              })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-7144" />
              </div>
            </div>
          </div>
          {/* Dates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-7145">
            <div data-id="element-7146">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-7147">
                Issue Date *
              </label>
              <input type="date" required value={formData.issueDate} onChange={e => setFormData({
              ...formData,
              issueDate: e.target.value
            })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-7148" />
            </div>
            <div data-id="element-7149">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-7150">
                Due Date *
              </label>
              <input type="date" required value={formData.dueDate} onChange={e => setFormData({
              ...formData,
              dueDate: e.target.value
            })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-7151" />
            </div>
          </div>
          <div className="mb-6" data-id="element-7152">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-7153">
              Description
            </label>
            <input type="text" value={formData.description} onChange={e => setFormData({
            ...formData,
            description: e.target.value
          })} placeholder="Brief description of the invoice" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-7154" />
          </div>
        </div>
        {/* Invoice Items */}
        <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-7155">
          <div className="flex items-center justify-between mb-4" data-id="element-7156">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-7157">Invoice Items</h3>
            <button type="button" onClick={addItem} className="flex items-center gap-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg" data-id="element-7158">
              <PlusIcon size={18} data-id="element-7159" />
              Add Item
            </button>
          </div>
          <div className="space-y-4" data-id="element-7160">
            {items.map((item, index) => <div key={item.id} className="grid grid-cols-12 gap-4 items-start" data-id="element-7161">
                <div className="col-span-12 md:col-span-5" data-id="element-7162">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-7163">
                    Description
                  </label>
                  <input type="text" value={item.description} onChange={e => updateItem(item.id, 'description', e.target.value)} placeholder="Item description" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-7164" />
                </div>
                <div className="col-span-4 md:col-span-2" data-id="element-7165">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-7166">
                    Quantity
                  </label>
                  <input type="number" min="1" value={item.quantity} onChange={e => updateItem(item.id, 'quantity', parseInt(e.target.value))} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-7167" />
                </div>
                <div className="col-span-4 md:col-span-2" data-id="element-7168">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-7169">
                    Unit Price
                  </label>
                  <input type="number" min="0" step="0.01" value={item.unitPrice} onChange={e => updateItem(item.id, 'unitPrice', parseFloat(e.target.value))} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-7170" />
                </div>
                <div className="col-span-3 md:col-span-2" data-id="element-7171">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-7172">
                    Total
                  </label>
                  <div className="px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900" data-id="element-7173">
                    Rs. {item.total.toLocaleString('en-LK')}
                  </div>
                </div>
                <div className="col-span-1 flex items-end" data-id="element-7174">
                  <button type="button" onClick={() => removeItem(item.id)} disabled={items.length === 1} className="p-2 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-7175">
                    <TrashIcon size={20} data-id="element-7176" />
                  </button>
                </div>
              </div>)}
          </div>
          {/* Total */}
          <div className="mt-6 pt-6 border-t border-gray-200" data-id="element-7177">
            <div className="flex justify-end" data-id="element-7178">
              <div className="w-full max-w-xs space-y-2" data-id="element-7179">
                <div className="flex justify-between py-2" data-id="element-7180">
                  <span className="text-sm text-gray-600" data-id="element-7181">Subtotal:</span>
                  <span className="text-sm font-medium text-gray-900" data-id="element-7182">
                    Rs. {calculateSubtotal().toLocaleString('en-LK')}
                  </span>
                </div>
                <div className="flex justify-between py-3 border-t-2 border-gray-200" data-id="element-7183">
                  <span className="text-lg font-semibold text-gray-900" data-id="element-7184">
                    Total:
                  </span>
                  <span className="text-lg font-bold text-gray-900" data-id="element-7185">
                    Rs. {calculateSubtotal().toLocaleString('en-LK')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Notes */}
        <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-7186">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-7187">
            Notes
          </label>
          <textarea value={formData.notes} onChange={e => setFormData({
          ...formData,
          notes: e.target.value
        })} rows={4} placeholder="Additional notes or payment terms..." className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-7188" />
        </div>
        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3" data-id="element-7189">
          <button type="button" onClick={() => navigate('/invoices')} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-7190">
            Cancel
          </button>
          <button type="button" onClick={handleSaveAsDraft} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-7191">
            Save as Draft
          </button>
          <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-id="element-7192">
            {isEditMode ? 'Update Invoice' : 'Create Invoice'}
          </button>
        </div>
      </form>
    </div>;
}